import { lockScroll, unlockScroll } from '@theme/utilities';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const root = () => window.Shopify?.routes?.root || '/';
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const sheetQuery = window.matchMedia('(max-width: 749px)');
const isCartDrawer = (node) => node instanceof HTMLElement && node.id === 'cart-drawer';
const getDrawer = () => document.getElementById('cart-drawer');
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

let pointerInteraction = false;
document.addEventListener('pointerdown', () => (pointerInteraction = true), true);
document.addEventListener('keydown', () => (pointerInteraction = false), true);

const getStatus = () => {
  const panel = getDrawer()?.querySelector('dialog');
  if (!panel) return null;
  let status = panel.querySelector('.kame-cart-status');
  if (!status) {
    status = document.createElement('div');
    status.className = 'kame-cart-status';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    panel.append(status);
  }
  return status;
};

const announce = (message) => {
  const status = getStatus();
  if (status) status.textContent = message;
};

getStatus();

document.addEventListener('theme-drawer:open', (event) => {
  if (!isCartDrawer(event.target)) return;
  lockScroll(event.target);
  getStatus();
  if (pointerInteraction) event.target.querySelector('dialog')?.focus({ preventScroll: true });
});

document.addEventListener('theme-drawer:close', (event) => {
  if (isCartDrawer(event.target)) unlockScroll(event.target);
});

document.addEventListener('click', (event) => {
  const drawer = getDrawer();
  if (!drawer?.hasAttribute('open')) return;
  if (event.target !== document.body && event.target !== document.documentElement) return;
  drawer.close();
});

let drag = null;

document.addEventListener('pointerdown', (event) => {
  if (!sheetQuery.matches || event.button !== 0) return;
  const target = event.target instanceof Element ? event.target : null;
  const header = target?.closest('#cart-drawer .theme-drawer__header');
  if (!header || target.closest('button, a, input')) return;
  const panel = header.closest('dialog');
  if (!panel) return;
  drag = { panel, header, id: event.pointerId, startY: event.clientY, startT: event.timeStamp, dy: 0 };
  header.setPointerCapture(event.pointerId);
  panel.classList.add('kame-cart-dragging');
});

document.addEventListener('pointermove', (event) => {
  if (!drag || event.pointerId !== drag.id) return;
  drag.dy = Math.max(0, event.clientY - drag.startY);
  if (!reducedMotion.matches) drag.panel.style.translate = `0 ${drag.dy}px`;
  else drag.panel.style.removeProperty('translate');
});

const endDrag = (event) => {
  if (!drag || event.pointerId !== drag.id) return;
  const { panel, header, dy, startT } = drag;
  drag = null;
  if (header.hasPointerCapture(event.pointerId)) header.releasePointerCapture(event.pointerId);
  panel.classList.remove('kame-cart-dragging');
  const velocity = dy / Math.max(1, event.timeStamp - startT);
  if (event.type === 'pointerup' && (dy > 48 || (dy > 12 && velocity > 0.5))) {
    getDrawer()
      ?.close()
      .finally(() => panel.style.removeProperty('translate'));
    return;
  }
  panel.style.removeProperty('translate');
};

document.addEventListener('pointerup', endDrag);
document.addEventListener('pointercancel', endDrag);

class KameCartRecs extends HTMLElement {
  #items = [];
  #request = 0;
  #holdUntil = 0;
  #observer = new MutationObserver(() => this.#restore());

  connectedCallback() {
    this.#observer.observe(this, { childList: true, subtree: true });
    this.addEventListener('click', this.#onClick);
    this.load();
  }

  disconnectedCallback() {
    this.#observer.disconnect();
    this.removeEventListener('click', this.#onClick);
  }

  get list() {
    return this.querySelector('.kame-cart-row');
  }

  #restore() {
    const list = this.list;
    if (list && !list.children.length && this.#items.length) list.replaceChildren(...this.#items);
  }

  async load() {
    const request = ++this.#request;
    const limit = Number(this.dataset.limit) || 6;
    try {
      const cart = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const first = cart.items?.[0];
      if (!first) return this.#render([], request);
      const inCart = new Set(cart.items.map((item) => String(item.product_id)));
      let items = await this.#fetch(first.product_id, 'complementary', limit + inCart.size, inCart);
      if (!items.length) items = await this.#fetch(first.product_id, 'related', limit + inCart.size, inCart);
      const hold = this.#holdUntil - performance.now();
      if (hold > 0) await wait(hold);
      this.#render(items.slice(0, limit), request);
    } catch {
      this.#render([], request);
    }
  }

  async #fetch(productId, intent, limit, inCart) {
    const params = new URLSearchParams({
      product_id: String(productId),
      limit: String(Math.min(limit, 10)),
      intent,
      section_id: 'kame-cart-recommendations',
    });
    const response = await fetch(`${this.dataset.url}?${params}`);
    if (!response.ok) return [];
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return [...doc.querySelectorAll('.kame-cart-card')].filter((card) => !inCart.has(card.dataset.productId));
  }

  #render(items, request) {
    if (request !== this.#request) return;
    this.#items = items.map((node) => document.importNode(node, true));
    this.list?.replaceChildren(...this.#items);
  }

  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('.kame-cart-card__add') : null;
    if (button) addFromCard(button, () => (this.#holdUntil = performance.now() + 900));
  };
}

if (!customElements.get('kame-cart-recs')) customElements.define('kame-cart-recs', KameCartRecs);

async function addFromCard(button, onAdded) {
  if (button.getAttribute('aria-busy') === 'true' || button.classList.contains('is-added')) return;
  announce('');
  const previousCount = Number(getDrawer()?.querySelector('[data-cart-count]')?.dataset.cartCount) || 0;
  const sections = [...document.querySelectorAll('cart-items-component')]
    .map((element) => element.dataset.sectionId)
    .filter(Boolean);
  const body = new FormData();
  body.set('id', button.dataset.variantId);
  body.set('quantity', '1');
  if (sections.length) body.set('sections', sections.join(','));

  button.setAttribute('aria-busy', 'true');
  button.classList.remove('is-error');
  let added;
  try {
    const response = await fetch(`${root()}cart/add.js`, {
      method: 'POST',
      body,
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    });
    added = await response.json();
    if (!response.ok || added.status) throw new Error(added.description || added.message);
  } catch (error) {
    button.classList.add('is-error');
    announce(error.message || 'Unable to add this item. Please try again.');
    button.removeAttribute('aria-busy');
    return;
  }

  button.classList.add('is-added');
  button.setAttribute('aria-disabled', 'true');
  onAdded?.();
  const title =
    added.product_title ||
    added.title ||
    button.closest('.kame-cart-card')?.querySelector('.kame-cart-card__title')?.textContent.trim() ||
    'Item';
  announce(`${title} added to cart`);
  let cart;
  try {
    const response = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to refresh cart');
    cart = await response.json();
    if (!Array.isArray(cart.items)) throw new Error('Invalid cart response');
  } catch {
    cart = null;
  }

  const deferred = CartLinesUpdateEvent.createPromise();
  const sectionHTML = added.sections?.['cart-drawer-section'];
  const sectionCount = sectionHTML
    ? new DOMParser().parseFromString(sectionHTML, 'text/html').querySelector('[data-cart-count]')?.dataset.cartCount
    : undefined;
  (getDrawer() || document).dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: String(button.dataset.variantId), quantity: 1 }],
      promise: deferred.promise,
    })
  );
  deferred.resolve({
    ...(cart ? { cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) } : {}),
    detail: {
      ...(cart ? { items: cart.items } : {}),
      source: 'kame-cart-drawer',
      itemCount: cart?.item_count ?? (sectionCount === undefined ? previousCount + 1 : Number(sectionCount)),
      sections: added.sections,
      didError: false,
    },
  });
  button.removeAttribute('aria-busy');
}

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('.kame-cart-empty .kame-cart-card__add') : null;
  if (button) addFromCard(button);
});

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(() => document.querySelectorAll('kame-cart-recs').forEach((recs) => recs.load()))
    .catch(() => {});
});
